import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X, Mail, Phone, Star, Users } from "lucide-react";
import api from "@/lib/api";
import { Card, Empty, Spinner } from "@/components/common";

const inp = "w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]";
const empty = () => ({ name: "", role: "", email: "", phone: "", is_primary: false, is_billing: false, is_technical: false, can_approve: false, note: "" });
const FLAGS = [
  ["is_primary", "Hauptkontakt", "border-[#A3E635]/30 bg-[#A3E635]/10 text-[#A3E635]"],
  ["is_billing", "Rechnungskontakt", "border-sky-400/30 bg-sky-400/10 text-sky-300"],
  ["is_technical", "Technischer Kontakt", "border-purple-400/30 bg-purple-400/10 text-purple-300"],
  ["can_approve", "Freigaberecht", "border-amber-400/30 bg-amber-400/10 text-amber-300"],
];

export default function ContactsTab({ clientId, readOnly = false }) {
  const [list, setList] = useState(null);
  const [modal, setModal] = useState(null);
  const [f, setF] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [delId, setDelId] = useState(null);

  const load = () => api.get(`/clients/${clientId}/contacts`).then((r) => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);
  if (!list) return <Spinner />;

  const openNew = () => { setF(empty()); setModal({}); };
  const openEdit = (c) => { setF({ ...empty(), ...c }); setModal(c); };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name erforderlich"); return; }
    setBusy(true);
    try {
      if (modal && modal.id) await api.put(`/contacts/${modal.id}`, f);
      else await api.post(`/clients/${clientId}/contacts`, f);
      toast.success("Kontaktperson gespeichert"); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/contacts/${id}`); toast.success("Kontaktperson gelöscht"); setDelId(null); load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };

  return (
    <div className="space-y-4" data-testid="contacts-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-bold">Kontaktpersonen</h3>
          <p className="text-xs text-gray-500">Ansprechpersonen beim Kunden. Getrennt von den Cockpit-Zugängen (Logins) weiter unten.</p>
        </div>
        {!readOnly && <button onClick={openNew} data-testid="contact-new-btn"
          className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Kontaktperson hinzufügen
        </button>}
      </div>

      {list.length === 0 ? (
        <Empty text="Noch keine Kontaktpersonen erfasst. Fügen Sie Ansprechpersonen mit Rolle und Kontaktdaten hinzu." />
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {list.map((c) => (
            <Card key={c.id} className="p-4" data-testid={`contact-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-1.5">{c.is_primary && <Star className="w-3.5 h-3.5 text-[#A3E635] fill-[#A3E635]" />}{c.name}</div>
                  {c.role && <div className="text-xs text-gray-500">{c.role}</div>}
                </div>
                {!readOnly && <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(c)} data-testid={`contact-edit-${c.id}`} className="p-1.5 rounded-lg border border-white/10 text-gray-400 hover:bg-white/5"><Pencil className="w-3.5 h-3.5" /></button>
                  {delId === c.id ? (
                    <button onClick={() => remove(c.id)} data-testid={`contact-confirm-del-${c.id}`} className="text-xs font-bold text-red-400 px-1.5">Löschen?</button>
                  ) : (
                    <button onClick={() => setDelId(c.id)} data-testid={`contact-del-${c.id}`} className="p-1.5 rounded-lg border border-white/10 text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>}
              </div>
              <div className="mt-2 space-y-1 text-xs">
                {c.email && <div className="flex items-center gap-1.5 text-gray-300"><Mail className="w-3 h-3 text-gray-500" /> <span className="break-all">{c.email}</span></div>}
                {c.phone && <div className="flex items-center gap-1.5 text-gray-300"><Phone className="w-3 h-3 text-gray-500" /> {c.phone}</div>}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {FLAGS.filter(([k]) => c[k]).map(([k, label, tone]) => (
                  <span key={k} className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${tone}`}>{label}</span>
                ))}
              </div>
              {c.note && <p className="text-xs text-gray-400 mt-2">{c.note}</p>}
            </Card>
          ))}
        </div>
      )}

      {modal !== null && !readOnly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="contact-modal">
          <div className="bg-[#121217] border border-white/10 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold">{modal.id ? "Kontaktperson bearbeiten" : "Kontaktperson hinzufügen"}</h3>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-400 mb-1 block">Name</label><input className={inp} data-testid="contact-f-name" value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
              <div><label className="text-xs text-gray-400 mb-1 block">Rolle / Funktion</label><input className={inp} data-testid="contact-f-role" value={f.role} onChange={(e) => set("role", e.target.value)} placeholder="z. B. Geschäftsführung" /></div>
              <div><label className="text-xs text-gray-400 mb-1 block">E-Mail</label><input className={inp} data-testid="contact-f-email" value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
              <div><label className="text-xs text-gray-400 mb-1 block">Telefon</label><input className={inp} data-testid="contact-f-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {FLAGS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2">
                  <input type="checkbox" data-testid={`contact-f-${k}`} checked={!!f[k]} onChange={(e) => set(k, e.target.checked)} /> {label}
                </label>
              ))}
            </div>
            <div className="mt-3"><label className="text-xs text-gray-400 mb-1 block">Notiz</label><textarea className={`${inp} min-h-[60px]`} data-testid="contact-f-note" value={f.note} onChange={(e) => set("note", e.target.value)} /></div>
            <div className="flex items-center gap-3 mt-5">
              <button onClick={save} disabled={busy} data-testid="contact-f-save"
                className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
                <Save className="w-4 h-4" /> Speichern
              </button>
              <button onClick={() => setModal(null)} className="text-sm text-gray-400 hover:text-white px-3 py-2">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
